import React, { useState, useEffect } from "react";
import { MultiSelect, Message, Button, Switch, Spin, Input } from "../../../BP2Components";
import { useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import IntlMessages from "../../../../util/IntlMessages";
import "./LineChartStyle.less";
import { setWidgetInSheetParams } from "../../../../appRedux/actions/Boards";
import {
   getRandomColor,
   isNumeric,
   removeDuplicateObjects,
   checkFieldStructureAndType
} from "./LineChartScripts";

interface LineChartSettingsProps {
   allDataObjects: any;
   value: any;
   data: any;
   loading: boolean;
}
interface ChartItemInterface {
   id: string | number;
   value: string;
   label: string;
}

const LineChartSettings: React.FC<LineChartSettingsProps> = ({
   value,
   data,
   allDataObjects,
   loading
}) => {
   const [listIdError, setListIdError] = useState(false);
   const [chartItems, setChartItems] = useState<Array<ChartItemInterface>>([]);

   const [settings, setSettings] = useState({
      ...value
   });

   const dispatch = useDispatch();
   const intl = useIntl();

   const technamesList = data?.data?.default?.columns?.map((singleColumn) => ({
      value: singleColumn?.options?.techName,
      label: singleColumn.label
   }));

   // here we creating list of object to select items for chart (items to dispaly)
   useEffect(() => {
      // first we need to find column, that user declares to contain items (for example: users)
      const valueTechname = technamesList?.find(
         (obj) => obj.value === settings?.userTechName
      )?.label;

      // next we are creating list of this items
      const valuesFromApi = allDataObjects?.map((item) => item?.[valueTechname]);

      const uniqueIds = {};

      // lists contains many elements for one (for example) user. So we need to filter it, to prevent duplicated items
      const uniqueValues = valuesFromApi?.filter((item) => {
         if (!uniqueIds[item?.id]) {
            // if item (for example user) is not in our list already, we are adding it to uniqueIds object
            uniqueIds[item?.id] = true;
            return true;
         }
         return false;
      });

      // since uniqueValues contains filtered items, we need to set it to state, to pass them to select, but first we need to check if our state don`t have any of items already
      if (chartItems?.length === 0 && uniqueValues) {
         setChartItems(uniqueValues);
      } else if (chartItems?.length > 0 && uniqueValues) {
         const newItems = uniqueValues.filter(
            (value) => !chartItems.some((item) => item?.id === value?.id)
         );

         setChartItems((prevItems) => [...prevItems, ...newItems]);
      }
   }, [allDataObjects, settings]);

   const itemsList: ChartItemInterface[] = (chartItems || [])
      .map((item) => ({
         id: String(item?.id || ""),
         value: String(item?.id || ""),
         label: String(item?.value || "")
      }))
      .filter((item) => item.id !== "");

   const handleSubmit = () => {
      try {
         dispatch(setWidgetInSheetParams(settings));
         Message.success(intl.formatMessage({ id: "global.saved" }));
      } catch {
         Message.error(intl.formatMessage({ id: "global.error" }));
      }
   };

   const handleChange = (value, key) => {
      if (key !== "listId") {
         setSettings((prevState) => ({
            ...prevState,
            [key]: value?.value ? value?.value : value
         }));
      }

      if (key === "users") {
         const usersWithColor = value?.map((object) => ({
            ...object,
            userColor: object.userColor || getRandomColor()
         }));
         setSettings((prevState) => ({
            ...prevState,
            [key]: usersWithColor?.value ? usersWithColor?.value : usersWithColor
         }));
      }

      if (key === "listId") {
         setListIdError(false);
         if (isNumeric(value)) {
            setSettings((prevState) => ({
               ...prevState,
               listId: value
            }));
         } else {
            setListIdError(true);
         }
      }

      if (key === "userTechName") {
         if (checkFieldStructureAndType(allDataObjects, value?.label) !== undefined) {
            Message.error(
               intl.formatMessage({ id: "structure.error.check.console.for.more.info" })
            );
            console.log(checkFieldStructureAndType(allDataObjects, value?.label));
         }
      }
   };

   return (
      <div
         style={{
            display: "flex",
            flexDirection: "column",
            position: "relative"
         }}
      >
         {loading ? <Spin /> : null}

         <div
            key="listId"
            style={{ display: "flex", flexDirection: "row", padding: "20px", alignItems: "center" }}
         >
            <label style={{ width: "170px" }}>
               {" "}
               <IntlMessages id="lineChart.select.list.id" />
            </label>
            {/* @ts-ignore */}
            <Input
               style={{ width: "65%", marginLeft: "30px", borderColor: "#cccccc" }}
               size="default"
               name="Select list ID"
               defaultValue={settings?.listId}
               onBlur={(e) => {
                  handleChange(e.target.value, "listId");
               }}
            />
         </div>
         {listIdError ? (
            <div className="list-id-error" style={{ color: "red" }}>
               <IntlMessages id="error.list.id.must.be.number" />
            </div>
         ) : null}
         <div
            key="horizontalValue"
            style={{ display: "flex", flexDirection: "row", padding: "20px", alignItems: "center" }}
         >
            <label style={{ width: "170px" }}>
               <IntlMessages id="lineChart.select.horizontal.column" />
            </label>
            {/* @ts-ignore */}
            <MultiSelect
               hideArrow={true}
               closeMenuOnSelect={true}
               isMulti={false}
               options={technamesList && technamesList}
               name="Select horizontal value"
               value={
                  technamesList?.find((obj) => obj.value === settings?.columnXTechName)?.value
                     ? technamesList?.find((obj) => obj.value === settings?.columnXTechName)?.value
                     : null
               }
               placeholder={
                  technamesList?.find((obj) => obj.value === settings?.columnXTechName)?.label
                     ? technamesList?.find((obj) => obj.value === settings?.columnXTechName)?.label
                     : null
               }
               onChange={(e) => {
                  handleChange(e, "columnXTechName");
               }}
               size="medium"
            />
         </div>
         <div
            key="ratingColumn"
            style={{ display: "flex", flexDirection: "row", padding: "20px", alignItems: "center" }}
         >
            <label style={{ width: "170px" }}>
               {" "}
               <IntlMessages id="lineChart.select.rating.column" />
            </label>
            {/* @ts-ignore */}
            <MultiSelect
               hideArrow={true}
               closeMenuOnSelect={true}
               isMulti={false}
               options={technamesList && technamesList}
               name="Select horizontal value"
               value={
                  technamesList?.find((obj) => obj.value === settings?.ratingColumnTechName)?.value
                     ? technamesList?.find((obj) => obj.value === settings?.ratingColumnTechName)
                          ?.value
                     : null
               }
               placeholder={
                  technamesList?.find((obj) => obj.value === settings?.ratingColumnTechName)?.label
                     ? technamesList?.find((obj) => obj.value === settings?.ratingColumnTechName)
                          ?.label
                     : null
               }
               onChange={(e) => {
                  handleChange(e, "ratingColumnTechName");
               }}
               size="medium"
            />
         </div>
         <div
            key="userColumn"
            style={{ display: "flex", flexDirection: "row", padding: "20px", alignItems: "center" }}
         >
            <label style={{ width: "170px" }}>
               {" "}
               <IntlMessages id="lineChart.select.item.column" />{" "}
            </label>
            {/* @ts-ignore */}
            <MultiSelect
               hideArrow={true}
               closeMenuOnSelect={true}
               isMulti={false}
               options={technamesList && technamesList}
               name="Select horizontal value"
               value={
                  technamesList?.find((obj) => obj.value === settings?.userTechName)?.value
                     ? technamesList?.find((obj) => obj.value === settings?.userTechName)?.value
                     : null
               }
               placeholder={
                  technamesList?.find((obj) => obj.value === settings?.userTechName)?.label
                     ? technamesList?.find((obj) => obj.value === settings?.userTechName)?.label
                     : null
               }
               onChange={(e) => {
                  handleChange(e, "userTechName");
               }}
               size="medium"
            />
         </div>
         <div
            key="users"
            style={{
               display: "flex",
               flexDirection: "row",
               padding: "20px",
               alignItems: "center"
            }}
         >
            <label style={{ width: "170px" }}>
               <IntlMessages id="lineChart.select.items" />
            </label>
            {/* @ts-ignore */}
            <MultiSelect
               isMulti={true}
               isSearchable={true}
               hideArrow={true}
               closeMenuOnSelect={true}
               defaultValue={value?.users}
               name="Select users"
               options={removeDuplicateObjects(itemsList)}
               onChange={(e) => {
                  handleChange(e, "users");
               }}
               size="medium"
            />
         </div>
         <div
            key="sumSwithch"
            style={{
               display: "flex",
               flexDirection: "row",
               padding: "20px",
               alignItems: "center"
            }}
         >
            <label style={{ width: "170px" }}>
               {" "}
               <IntlMessages id="lineChart.sum.ratings" />
            </label>

            <div style={{ paddingRight: "10px" }}>
               <Switch
                  defaultChecked={value?.sumRatings}
                  onChange={(e) => {
                     handleChange(e, "sumRatings");
                  }}
               />
            </div>
         </div>
         <Button
            style={{ position: "absolute", right: "20px" }}
            onClick={handleSubmit}
            buttonType="primary"
            htmlType="submit"
         >
            <IntlMessages id="global.preview" />
         </Button>
      </div>
   );
};

export default LineChartSettings;

/* This select might be used when vertical value is other then numbers */

/* <div
            key="verticalValue"
            style={{ display: "flex", flexDirection: "row", padding: "20px", alignItems: "center" }}
         >
            <label style={{ width: "170px" }}>
               <IntlMessages id="lineChart.select.vertical.column" />
            </label> */

/* @ts-ignore */

/* <MultiSelect
               hideArrow={true}
               closeMenuOnSelect={true}
               isMulti={false}
               options={technamesList && technamesList}
               name="Select vertical value"
               value={
                  technamesList?.find((obj) => obj.value === settings?.columnYTechName)?.value
                     ? technamesList?.find((obj) => obj.value === settings?.columnYTechName)?.value
                     : null
               }
               placeholder={
                  technamesList?.find((obj) => obj.value === settings?.columnYTechName)?.label
                     ? technamesList?.find((obj) => obj.value === settings?.columnYTechName)?.label
                     : null
               }
               onChange={(e) => {
                  handleChange(e, "columnYTechName");
               }}
               size="medium"
            /> */

/* </div> */
